import { ArrowLeft, ArrowRight, CheckCircle2, Compass, HeartHandshake } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase/client";

type VolunteerValues = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  availability: string;
  message: string;
  interests: string[];
  consent: boolean;
};

const content: Record<
  string,
  { eyebrow: string; title: string; sections: Array<[string, ReactNode]> }
> = {
  "/about": {
    eyebrow: "About the Program",
    title: "Every light begins with someone willing to guide the way.",
    sections: [
      [
        "Our purpose",
        "Guiding Stars is the internal operations, recognition, and engagement program of The Guided Pathway Foundation. It brings our volunteers and team together in one shared space to learn, serve, participate in Foundation programs and events, and be recognized for the difference they make.",
      ],
      [
        "What it means to be a Guiding Star",
        "A Guiding Star is someone who chooses to use their time, strengths, and compassion to help create stronger individuals, families, and communities. Through meaningful opportunities to learn and serve, members grow alongside the Foundation while helping extend its mission into the community.",
      ],
      [
        "Why the stars",
        <>
          For generations, people have looked to the stars as points of reference when finding their
          way. Guiding Stars carries that idea forward through service:{" "}
          <strong>
            our volunteers are the stars others can look to for encouragement, connection, and
            guidance.
          </strong>{" "}
          As members learn, contribute, and reach meaningful milestones, stars within their Atlas
          illuminate to represent their journey and impact.
        </>,
      ],
      [
        "Part of something bigger",
        "Every Guiding Star is part of The Guided Pathway Foundation's mission to equip individuals, families, and communities with emotional intelligence tools that build resilience, strengthen wellbeing, and help prevent emotional crises before they occur. Together, individual acts of service become something larger: a growing network of people helping create healthier, more connected communities.",
      ],
    ],
  },
  "/privacy": {
    eyebrow: "Privacy",
    title: "Your service record belongs to you.",
    sections: [
      [
        "Information we collect",
        "The program may store account details, program assignments, service records, submissions, evidence, reflections, recognition history, and communication preferences. Volunteer-interest forms collect only the details you choose to provide.",
      ],
      [
        "How information is used",
        "Information supports account administration, contribution review, program operations, recognition, impact reporting, safety, and communications you have agreed to receive.",
      ],
      [
        "Public visibility",
        "Profiles are private by default. You control whether a public profile exists and whether your name, photo, story, and aggregate statistics may appear. Private reflections and evidence are never made public through a profile setting.",
      ],
      [
        "Access and retention",
        "Role-based access limits staff to the records required for their work. Administrative changes are logged. Contact the Foundation to request correction, export, deactivation, or deletion where legally and operationally permitted.",
      ],
      [
        "Security",
        "Production data is protected by authenticated access, row-level security, private file storage, and least-privilege policies. No system can promise absolute security; suspected incidents should be reported promptly.",
      ],
    ],
  },
  "/accessibility": {
    eyebrow: "Accessibility",
    title: "The sky is for everyone.",
    sections: [
      [
        "Our standard",
        "We aim to meet WCAG 2.2 Level AA across public and authenticated experiences, including keyboard operation, visible focus, meaningful landmarks, sufficient contrast, accessible names, and understandable errors.",
      ],
      [
        "Visual alternatives",
        "Constellation graphics include a complete text alternative. Motion respects reduced-motion preferences, and the experience never relies on color, animation, or a visual star map alone to communicate meaning.",
      ],
      [
        "Supported interaction",
        "Pages are designed for keyboard navigation, screen readers, zoom, reflow, high contrast, touch, and viewports from small mobile screens through large displays.",
      ],
      [
        "Need help?",
        "If a barrier prevents you from using the program, contact the Foundation with the page, device, browser, and task you were attempting. We will provide an accessible alternative and work to correct the issue.",
      ],
    ],
  },
  "/terms": {
    eyebrow: "Terms of Use",
    title: "Clear expectations, written plainly.",
    sections: [
      [
        "Using the program",
        "Use the site lawfully and provide accurate information. Do not impersonate another person, interfere with the service, upload harmful material, or access records you are not authorized to view.",
      ],
      [
        "Accounts and decisions",
        "Access is invitation- and approval-based. The Foundation may change roles, suspend access, correct records, or revoke recognition when needed for safety, accuracy, policy compliance, or program integrity. Material changes retain an audit record.",
      ],
      [
        "Content and consent",
        "You keep ownership of content you submit. You grant the Foundation permission to process it for program operations. Public stories, photos, and profiles require the applicable consent and may be withdrawn for future publication.",
      ],
      [
        "Recognition",
        "Stars document verified contributions under current program definitions. They are not credentials, employment promises, financial awards, rankings, or measures of personal value.",
      ],
      [
        "Availability and changes",
        "The Foundation may maintain, improve, or discontinue features and may update these terms. Material updates will be communicated through the site or account notices.",
      ],
    ],
  },
};

function VolunteerPage() {
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<VolunteerValues>();
  const submit = handleSubmit(async (values) => {
    setState("saving");
    if (!supabase) {
      setState("error");
      return;
    }
    const { error } = await supabase.from("volunteer_interests").insert({
      first_name: values.firstName.trim(),
      last_name: values.lastName.trim(),
      email: values.email.trim().toLowerCase(),
      phone: values.phone.trim() || null,
      interests: values.interests ?? [],
      availability: values.availability.trim() || null,
      message: values.message.trim() || null,
      consent_to_contact: values.consent,
    });
    if (error) {
      setState("error");
      return;
    }
    reset();
    setState("saved");
  });
  return (
    <section className="public-content-page">
      <div className="site-shell public-content-grid">
        <div>
          <p className="eyebrow">Get Involved</p>
          <HeartHandshake size={46} strokeWidth={1.3} />
          <h1>Bring your time, care, and gifts to the path.</h1>
          <p className="public-lede">
            Tell us how you would like to help. A Foundation team member can follow up about current
            programs, events, and community needs.
          </p>
          <div className="content-sections">
            <article>
              <h2>Ways to contribute</h2>
              <p>
                Support events, mentor participants, offer professional skills, help with community
                outreach, or assist with program operations.
              </p>
            </article>
            <article>
              <h2>No commitment yet</h2>
              <p>
                Submitting this form expresses interest; it does not enroll you in a program or
                create an account.
              </p>
            </article>
            <article>
              <h2>What happens next</h2>
              <p>
                A Foundation team member will review your interests and contact you using the email
                address you provide. If there is a good fit, the team will explain the opportunity,
                send your Atlas invitation, and guide you through account approval and your first
                activity.
              </p>
              <p>
                Questions before you submit?{" "}
                <a href="mailto:info@theguidedpathwayfoundation.com">Contact the Foundation</a>.
              </p>
            </article>
          </div>
        </div>
        <form className="volunteer-form" onSubmit={(event) => void submit(event)} noValidate>
          <h2>Volunteer interest form</h2>
          <div className="form-pair">
            <label>
              First name
              <input
                autoComplete="given-name"
                {...register("firstName", { required: "Enter your first name" })}
              />
              {errors.firstName && <small role="alert">{errors.firstName.message}</small>}
            </label>
            <label>
              Last name
              <input
                autoComplete="family-name"
                {...register("lastName", { required: "Enter your last name" })}
              />
              {errors.lastName && <small role="alert">{errors.lastName.message}</small>}
            </label>
          </div>
          <label>
            Email
            <input
              type="email"
              autoComplete="email"
              {...register("email", {
                required: "Enter your email",
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email" },
              })}
            />
            {errors.email && <small role="alert">{errors.email.message}</small>}
          </label>
          <label>
            Phone (optional)
            <input type="tel" autoComplete="tel" {...register("phone")} />
          </label>
          <fieldset>
            <legend>Areas of interest</legend>
            {["Events", "Mentoring", "Outreach", "Program support", "Professional skills"].map(
              (item) => (
                <label className="check-row" key={item}>
                  <input type="checkbox" value={item} {...register("interests")} /> {item}
                </label>
              ),
            )}
          </fieldset>
          <label>
            Availability
            <textarea rows={2} {...register("availability")} />
          </label>
          <label>
            Anything else you would like us to know?
            <textarea rows={4} {...register("message")} />
          </label>
          <label className="check-row">
            <input
              type="checkbox"
              {...register("consent", { required: "Consent is required so we may respond" })}
            />{" "}
            I consent to being contacted about volunteering.
          </label>
          {errors.consent && <small role="alert">{errors.consent.message}</small>}
          <button className="button button-gold" disabled={state === "saving"}>
            {state === "saving" ? "Sending…" : "Send my interest"} <ArrowRight size={17} />
          </button>
          <div aria-live="polite">
            {state === "saved" && (
              <p className="form-success">
                <CheckCircle2 /> Thank you. Your interest was received. A Foundation team member
                will contact you by email with the next step.
              </p>
            )}
            {state === "error" && (
              <p className="form-error">
                We could not send the form. Please try again or contact the Foundation directly.
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}

export function PublicPage({ path }: { path: string }) {
  if (path === "/get-involved") return <VolunteerPage />;
  const page = content[path] ?? content["/about"];
  return (
    <section className="public-content-page">
      <div className="site-shell readable-content">
        <p className="eyebrow">{page.eyebrow}</p>
        <Compass size={44} strokeWidth={1.2} aria-hidden="true" />
        <h1>{page.title}</h1>
        <div className="content-sections">
          {page.sections.map(([title, body]) => (
            <article key={title}>
              <h2>{title}</h2>
              <p>{body}</p>
            </article>
          ))}
        </div>
        <div className="interior-actions">
          <Link className="button button-ghost" to="/">
            <ArrowLeft size={17} /> Return home
          </Link>
          <Link className="button button-gold" to="/get-involved">
            Get Involved <ArrowRight size={17} />
          </Link>
        </div>
      </div>
    </section>
  );
}
